import { Link } from "@repo/ui/Link/routerLink";
import type { FC, ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { tv } from "tailwind-variants";

export interface NavigationMenuItemProps {
  label: string;
  icon?: ReactNode;
  href?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  onPress?: () => void;
}

/** Shared styling for nav menu items and item-shaped triggers (e.g. search). */
export const navigationMenuItem = tv({
  slots: {
    root: "group flex min-h-8 w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left text-sm leading-5 font-normal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
    icon: "flex size-6 shrink-0 items-center justify-center rounded-md transition-colors",
    label: "min-w-0 flex-1 truncate",
  },
  variants: {
    isActive: {
      true: {
        root: "bg-card text-sidebar-foreground shadow-xs",
        icon: "text-primary",
      },
      false: {
        root: "text-sidebar-foreground hover:bg-sidebar-accent",
        icon: "text-inherit",
      },
    },
    isDisabled: {
      true: { root: "pointer-events-none text-muted-foreground opacity-60" },
    },
  },
});

export const NavigationMenuItem: FC<NavigationMenuItemProps> = ({
  label,
  icon,
  href,
  isActive = false,
  isDisabled = false,
  onPress,
}) => {
  const styles = navigationMenuItem({ isActive, isDisabled });

  const content = (
    <>
      {icon ? <span className={styles.icon()}>{icon}</span> : null}
      <span className={styles.label()}>{label}</span>
    </>
  );

  if (href && !isDisabled) {
    return (
      <Link
        href={href}
        aria-current={isActive ? "page" : undefined}
        className={styles.root()}
      >
        {content}
      </Link>
    );
  }

  return (
    <AriaButton
      onPress={onPress}
      aria-current={isActive ? "page" : undefined}
      isDisabled={isDisabled}
      className={styles.root()}
    >
      {content}
    </AriaButton>
  );
};
